import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { QuotationTypesEnums } from 'src/app/enum/quotation-enums';
import { ArticleModel } from 'src/app/models/archive/article.model';
import { BreakdownModel } from 'src/app/models/preliminaries/breakdown-model';
import { QuotationDetailModel } from 'src/app/models/preliminaries/quotation-detail.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { PriceListDetailService } from 'src/app/services/archive/price-list-detail.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { BreakdownService } from 'src/app/services/preliminaries/breakdown.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-breakdown',
  templateUrl: './register-breakdown.component.html',
  styleUrls: ['./register-breakdown.component.scss'],
  standalone: false
})
export class RegisterBreakdownComponent implements OnInit {
  @Input('quotationSolution')
  set changeQuotationSolution(changeSolution: QuotationDetailModel) {
    this.changeSolution(changeSolution);
  }
  @Input('breakdown')
  set changeQuotationBreakdown(changeBreakdown: BreakdownModel) {
    this.changeBreakdown(changeBreakdown);
  }

  @Input('typeQuotation')
  set changeTypeQuotation(changeType: number) {
    this.typeQuotation = changeType;
  }

  @Input() priceListId: number = 0;
  @Input() rateDiscountMax: number = 0;
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>();

  articleId: number = 0;
  breakdownId: number = 0;
  breakdownContract: BreakdownModel = new BreakdownModel();
  codeNameArticle: string = '';
  formBreakdown: FormGroup = new FormGroup({});
  lastValidUnitValue: number | null = null;
  listArticles: any[] = [];
  output$ = new Subject<any>();
  quotationSolutionContract: QuotationDetailModel = new QuotationDetailModel();
  quotationSolutionId: number = 0;
  typeQuotation: number = 0;

  constructor(private articleService: ArticleService,
    private breakdownService: BreakdownService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private priceListDetailService: PriceListDetailService,
    private toastHandlerService: ToastHandlerService,
  ) {
  }

  ngOnInit() {
    this.validateForm(new BreakdownModel());
  }

  calculateSubtotalValue() {
    const clientValue = this.formBreakdown.get("clientValue")?.value;
    const quantity = this.formBreakdown.get("quantity")?.value;
    const subtotalValue = clientValue * quantity;
    this.formBreakdown.controls["subtotal"].patchValue(subtotalValue);
  }

  async changeBreakdown(breakdown: BreakdownModel) {
    if (breakdown.articleId != 0) {
      this.breakdownContract = breakdown;
      this.breakdownContract.quotationId = this.quotationSolutionContract.quotationId;
      this.breakdownId = breakdown.breakdownId;
      this.validateForm(this.breakdownContract);
      await this.getArticle(breakdown.articleId)
    }
  }

  changeSolution(solution: QuotationDetailModel) {
    if (solution.quotationDetailId != 0) {
      this.quotationSolutionContract = solution;
      this.quotationSolutionId = this.quotationSolutionContract.quotationDetailId;
    }
  }

  clearForm() {
    this.codeNameArticle = "";
    this.validateForm(new BreakdownModel);
    this.isFormView.emit(false);
  }

  async getArticle(articleId: number | null) {
    let validArticle = null;
    let codeNameArticle = "";
    let article: ArticleModel = new ArticleModel()
    if (articleId != null) {
      article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        validArticle = articleId;
        codeNameArticle = `${article.code} - ${article.name}`;
      }
    }

    this.articleId = validArticle ?? 0;
    this.codeNameArticle = codeNameArticle;
    this.formBreakdown.controls["articleId"].patchValue(validArticle);
    if ([QuotationTypesEnums.newSale, QuotationTypesEnums.maintenance].includes(this.typeQuotation)) {
      this.formBreakdown.controls["clientValueList"].patchValue(article.saleValue);
    } else if (this.typeQuotation == QuotationTypesEnums.usedSale) {
      this.formBreakdown.controls["clientValueList"].patchValue(article.replacementValue);
    }
  }

  async getPriceListArticle(articleId: number) {
    const response = await firstValueFrom(this.priceListDetailService.getClientUnitValueByPriceListArticle(this.priceListId, articleId));
    this.formBreakdown.controls["clientValueList"].patchValue(response);
  }


  async send() {
    Object.keys(this.formBreakdown.controls).forEach(key => {
      const control = this.formBreakdown.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });

    if (this.formBreakdown.valid) {
      const modelBreakdown = this.formBreakdown.getRawValue();
      if (this.breakdownId == 0) {
        try {
          this.breakdownContract = await firstValueFrom(this.breakdownService.createBreakdown(modelBreakdown));
          this.toastHandlerService.showToast('success', 'El despiece fue creada exitosamente', '');
          this.breakdownId = this.breakdownContract.breakdownId;
        } catch (error) {
          console.error('Error al crear el despiece.', error);
        }
      } else {
        this.breakdownContract.unitValue = modelBreakdown.unitValue;
        this.breakdownContract.discountRate = modelBreakdown.discountRate;
        this.breakdownContract.clientValue = modelBreakdown.clientValue;
        this.breakdownContract.quantity = modelBreakdown.quantity;
        try {
          this.breakdownContract = await firstValueFrom(this.breakdownService.updateBreakdown(this.breakdownContract));
          this.toastHandlerService.showToast('success', 'El despiece fue modificada exitosamente', '');

        } catch (error) {
          console.error('Error al actualizar el despiece.', error);
        }
      }
      this.clearForm();
      this.isFormView.emit(true);
      this.updateListDetail.emit(true);
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages(): void {
    const controlMessages: Record<string, Record<string, string>> = {
      articleId: {
        required: 'El campo artículo es obligatorio.'
      },
      unitValue: {
        required: 'El campo valor unitario base es obligatorio.',
      },
      clientValue: {
        required: 'El campo valor cliente es obligatorio.',
      },
      quantity: {
        required: 'El campo cantidad es obligatorio.',
        min: 'Debe ingresar mínimo una cantidad.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formBreakdown.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  updateClientValue(clientValue: number) {
    let unitValue = this.formBreakdown.get("unitValue")?.value;
    if (unitValue == 0) {
      unitValue = clientValue;
    }

    const discountRate = unitValue > 0 ? 100 - (clientValue * 100 / unitValue) : 0;

    if (!this.validateDiscountRateForUser(discountRate)) {
      return;
    }

    this.formBreakdown.controls["unitValue"].patchValue(unitValue);
    this.formBreakdown.controls["discountRate"].patchValue(discountRate);
    this.calculateSubtotalValue();
  }

  updateDiscountRate(discountRate: number) {
    if (!this.validateDiscountRateForUser(discountRate)) {
      return;
    }
    const unitValue = this.formBreakdown.get("unitValue")?.value;
    const clientValue = unitValue - (unitValue * discountRate / 100);
    this.formBreakdown.controls["clientValue"].patchValue(clientValue);
  }

  updateUnitValue(unitValue: number) {
    const clientValueList = this.formBreakdown.get("clientValueList")?.value;

    if (unitValue < clientValueList) {
      this.toastHandlerService.showToast(
        'warning',
        'El valor unitario base no puede ser menor al valor de la lista',
        ''
      );
      this.formBreakdown.controls["unitValue"].patchValue(this.lastValidUnitValue, { emitEvent: false });
      return;
    }

    //Guarda el ultimo valor del art. para asignarlo de nuevo en caso de que no pase la validación
    this.lastValidUnitValue = unitValue;
    const discountRate = this.formBreakdown.get("discountRate")?.value;
    let clientValue = 0;

    if (discountRate > 0) {
      clientValue = unitValue - (unitValue * discountRate / 100);
    } else {
      clientValue = unitValue;
    }

    this.formBreakdown.controls["clientValue"].patchValue(clientValue);
  }

  validateDiscountRateForUser(discountRate: number) {
    if (discountRate > this.rateDiscountMax) {
      this.toastHandlerService.showToast('warning', `El descuento máximo permitido para el usuario actual es del ${this.rateDiscountMax} %`, '');
      return false;
    }
    return true;
  }

  async validateForm(breakdown: BreakdownModel) {
    this.formBreakdown = this.formBuilder.group({
      quotationId: [breakdown.quotationId, [Validators.required]],
      quotationDetailId: [breakdown.quotationDetailId, [Validators.required]],
      clientValueList: [breakdown.clientValueList],
      articleId: [breakdown.articleId, [Validators.required]],
      unitValue: [breakdown.unitValue, [Validators.required]],
      discountRate: [breakdown.discountRate, []],
      clientValue: [breakdown.clientValue, [Validators.required]],
      quantity: [breakdown.quantity, [Validators.required, Validators.min(1)]],
      subtotal: [[breakdown.clientValue * breakdown.quantity]],
    });

    this.formControlStateService.setControlState(this.formBreakdown, ['clientValueList', 'subtotal'], false);
    //Cuando editen no tiene el valor de la lista por lo que se debe consultar
    if (breakdown.breakdownId != 0 && breakdown.clientValueList == 0 && this.typeQuotation == QuotationTypesEnums.rental) {
      await this.getPriceListArticle(breakdown.articleId);
    }
    this.lastValidUnitValue = breakdown.unitValue;
  }

}
